import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Store, TrendingUp, DollarSign, Calendar, Clock, Bell, FileText, Wrench,
  BarChart3, ShoppingCart, ArrowUpRight, ArrowDownRight, Receipt, AlertCircle,
  CheckCircle2, Loader2, MessageSquare, Upload, Eye, Star, Phone, Mail,
  MapPin, FileCheck, AlertTriangle, Send, History, ClipboardList
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  urgent: "bg-red-500/20 text-red-400 border-red-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};

const STATUS_COLORS = {
  open: "bg-red-500/20 text-red-400",
  acknowledged: "bg-blue-500/20 text-blue-400",
  assigned: "bg-purple-500/20 text-purple-400",
  in_progress: "bg-yellow-500/20 text-yellow-400",
  resolved: "bg-green-500/20 text-green-400",
  closed: "bg-gray-500/20 text-gray-400",
  pending: "bg-yellow-500/20 text-yellow-400",
  paid: "bg-green-500/20 text-green-400",
  overdue: "bg-red-500/20 text-red-400",
  valid: "bg-green-500/20 text-green-400",
  expiring_soon: "bg-yellow-500/20 text-yellow-400",
  expired: "bg-red-500/20 text-red-400",
};

export default function TenantPortalPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notices, setNotices] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  
  // Dialog states
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showComplianceDialog, setShowComplianceDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form states
  const [requestForm, setRequestForm] = useState({
    category: "maintenance", title: "", description: "", priority: "medium", location_details: ""
  });
  const [complianceForm, setComplianceForm] = useState({
    document_type: "fire_noc", document_name: "", document_url: "", issue_date: "", expiry_date: ""
  });
  const [feedbackForm, setFeedbackForm] = useState({ rating: 5, feedback: "" });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get tenant dashboard
      const dashRes = await axios.get(`${API}/api/tenant/dashboard`, { headers });
      setDashboardData(dashRes.data);
      
      const shopId = dashRes.data?.shop?.id;
      const mallId = dashRes.data?.shop?.mall_id;
      
      if (shopId && mallId) {
        const [requestsRes, complianceRes, paymentsRes, noticesRes, perfRes] = await Promise.all([
          axios.get(`${API}/api/tenant-service/requests/${shopId}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/tenant-service/compliance/${shopId}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/tenant-service/payments/${shopId}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/tenant-service/notices/${mallId}?shop_id=${shopId}`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/tenant/shop/${shopId}/performance`, { headers }).catch(() => ({ data: null })),
        ]);
        
        setServiceRequests(requestsRes.data);
        setCompliance(complianceRes.data);
        setPayments(paymentsRes.data);
        setNotices(noticesRes.data);
        setPerformanceData(perfRes.data);
      }
    } catch (err) {
      console.error("Error fetching tenant data:", err);
      toast.error("Failed to load tenant data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.title || !requestForm.description) {
      toast.error("Please fill in title and description");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/tenant-service/requests`, {
        ...requestForm,
        shop_id: dashboardData?.shop?.id,
        mall_id: dashboardData?.shop?.mall_id
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Service request submitted successfully");
      setShowRequestDialog(false);
      setRequestForm({ category: "maintenance", title: "", description: "", priority: "medium", location_details: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadCompliance = async () => {
    if (!complianceForm.document_name || !complianceForm.document_url || !complianceForm.expiry_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/tenant-service/compliance`, {
        ...complianceForm,
        shop_id: dashboardData?.shop?.id
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Compliance document uploaded");
      setShowComplianceDialog(false);
      setComplianceForm({ document_type: "fire_noc", document_name: "", document_url: "", issue_date: "", expiry_date: "" });
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/api/tenant-service/requests/${selectedRequest.id}/feedback?rating=${feedbackForm.rating}&feedback=${encodeURIComponent(feedbackForm.feedback || '')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success("Feedback submitted");
      setShowFeedbackDialog(false);
      setSelectedRequest(null);
      setFeedbackForm({ rating: 5, feedback: "" });
      fetchAllData();
    } catch (err) {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value || 0);
  const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="tenant-portal-loading">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading tenant portal...</p>
        </div>
      </div>
    );
  }

  const { shop, sales_summary, rent_details } = dashboardData || {};

  return (
    <div className="space-y-6" data-testid="tenant-portal-enhanced">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tenant Portal</h1>
          <p className="text-muted-foreground">Manage your store, requests, compliance & payments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowRequestDialog(true)} data-testid="new-request-btn">
            <Send className="w-4 h-4 mr-2" />
            Raise Request
          </Button>
        </div>
      </div>

      {/* Store Info Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Store className="w-8 h-8 text-primary" />
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Store</p>
                <p className="font-semibold">{shop?.brand_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unit</p>
                <p className="font-semibold">{shop?.unit_number || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Mall</p>
                <p className="font-semibold">{shop?.mall_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Area</p>
                <p className="font-semibold">{formatNumber(shop?.carpet_area_sqft)} sq ft</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Floor</p>
                <p className="font-semibold">Floor {shop?.floor || 0}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Open Requests</p>
                <p className="text-2xl font-bold">{serviceRequests.filter(r => r.status !== 'resolved' && r.status !== 'closed').length}</p>
              </div>
              <ClipboardList className="w-8 h-8 text-orange-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Pending Payment</p>
                <p className="text-2xl font-bold">{formatCurrency(rent_details?.total_due)}</p>
              </div>
              <Receipt className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Compliance</p>
                <p className="text-2xl font-bold">{compliance.filter(c => c.status === 'valid').length}/{compliance.length}</p>
              </div>
              <FileCheck className="w-8 h-8 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Unread Notices</p>
                <p className="text-2xl font-bold">{notices.filter(n => !n.read_by?.includes(shop?.id)).length}</p>
              </div>
              <Bell className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="notices" data-testid="tab-notices">Notices</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Today's Sales</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(sales_summary?.today)}</p>
                  </div>
                  <ShoppingCart className="w-10 h-10 text-emerald-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">This Month</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(sales_summary?.this_month)}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {sales_summary?.growth_percent >= 0 ? (
                        <><ArrowUpRight className="w-4 h-4 text-emerald-500" /><span className="text-sm text-emerald-500">{sales_summary?.growth_percent}%</span></>
                      ) : (
                        <><ArrowDownRight className="w-4 h-4 text-red-500" /><span className="text-sm text-red-500">{Math.abs(sales_summary?.growth_percent)}%</span></>
                      )}
                    </div>
                  </div>
                  <TrendingUp className="w-10 h-10 text-blue-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Rent Due</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrency(rent_details?.total_due)}</p>
                    <Badge className={STATUS_COLORS[rent_details?.status]} variant="outline">{rent_details?.status}</Badge>
                  </div>
                  <DollarSign className="w-10 h-10 text-amber-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Lease Ends</p>
                    <p className="text-2xl font-bold mt-1">{rent_details?.lease_end || 'N/A'}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-purple-500/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {serviceRequests.slice(0, 5).length > 0 ? (
                  <div className="space-y-3">
                    {serviceRequests.slice(0, 5).map(req => (
                      <div key={req.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{req.title}</p>
                          <p className="text-xs text-muted-foreground">{req.ticket_number} | {req.category}</p>
                        </div>
                        <Badge className={STATUS_COLORS[req.status]}>{req.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No recent requests</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notices & Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                {notices.slice(0, 5).length > 0 ? (
                  <div className="space-y-3">
                    {notices.slice(0, 5).map(notice => (
                      <div key={notice.id} className={`p-3 rounded-lg border-l-4 ${notice.notice_type === 'alert' ? 'border-l-red-500 bg-red-500/10' : notice.notice_type === 'event' ? 'border-l-purple-500 bg-purple-500/10' : 'border-l-blue-500 bg-blue-500/10'}`}>
                        <p className="font-medium text-sm">{notice.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{notice.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No notices</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Service Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Service Requests ({serviceRequests.length})</h3>
            <Button onClick={() => setShowRequestDialog(true)} data-testid="create-request-btn">
              <Send className="w-4 h-4 mr-2" />
              New Request
            </Button>
          </div>

          <div className="grid gap-4">
            {serviceRequests.map(req => (
              <Card key={req.id} data-testid={`request-card-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${req.priority === 'urgent' ? 'bg-red-500/20' : 'bg-muted'}`}>
                        <Wrench className={`w-6 h-6 ${req.priority === 'urgent' ? 'text-red-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{req.title}</p>
                          <Badge className={PRIORITY_COLORS[req.priority]}>{req.priority}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{req.ticket_number} | {req.category}</p>
                        <p className="text-sm mt-2">{req.description}</p>
                        {req.resolution && (
                          <div className="mt-2 p-2 bg-green-500/10 rounded text-sm">
                            <p className="text-green-400 font-medium">Resolution:</p>
                            <p>{req.resolution}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={STATUS_COLORS[req.status]}>{req.status}</Badge>
                      <p className="text-xs text-muted-foreground mt-2">{new Date(req.created_at).toLocaleDateString()}</p>
                      {req.status === 'resolved' && !req.rating && (
                        <Button size="sm" variant="outline" className="mt-2" onClick={() => { setSelectedRequest(req); setShowFeedbackDialog(true); }}>
                          <Star className="w-3 h-3 mr-1" />
                          Rate
                        </Button>
                      )}
                      {req.rating && (
                        <div className="mt-2 text-yellow-500">
                          {"★".repeat(req.rating)}{"☆".repeat(5 - req.rating)}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {serviceRequests.length === 0 && (
            <Card className="p-12 text-center">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No service requests yet</p>
              <Button className="mt-4" onClick={() => setShowRequestDialog(true)}>Create First Request</Button>
            </Card>
          )}
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Rent Breakdown</CardTitle>
                <CardDescription>Current month charges</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Base Rent</span>
                  <span className="font-medium">{formatCurrency(rent_details?.base_rent)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">CAM Charges</span>
                  <span className="font-medium">{formatCurrency(rent_details?.cam_charges)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Revenue Share</span>
                  <span className="font-medium">{formatCurrency(rent_details?.revenue_share)}</span>
                </div>
                <div className="flex justify-between items-center py-2 font-bold text-lg">
                  <span>Total Due</span>
                  <span className="text-primary">{formatCurrency(rent_details?.total_due)}</span>
                </div>
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground">Due Date: <span className="font-medium text-foreground">{rent_details?.due_date}</span></p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Lease Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Lease Start</span>
                  <span className="font-medium">{rent_details?.lease_start}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Lease End</span>
                  <span className="font-medium">{rent_details?.lease_end}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Store Size</span>
                  <span className="font-medium">{formatNumber(shop?.carpet_area_sqft)} sq ft</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-muted-foreground">Rent/sq ft</span>
                  <span className="font-medium">{formatCurrency(shop?.rent_per_sqft)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              {payments.length > 0 ? (
                <div className="space-y-3">
                  {payments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">{payment.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">{payment.invoice_type} | Due: {payment.due_date}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(payment.amount)}</p>
                        <Badge className={STATUS_COLORS[payment.status]}>{payment.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-6">No payment history</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Compliance Documents ({compliance.length})</h3>
            <Button onClick={() => setShowComplianceDialog(true)} data-testid="upload-compliance-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {compliance.map(doc => (
              <Card key={doc.id} data-testid={`compliance-card-${doc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="p-2 bg-muted rounded-lg">
                      <FileCheck className="w-5 h-5 text-primary" />
                    </div>
                    <Badge className={STATUS_COLORS[doc.status]}>{doc.status}</Badge>
                  </div>
                  <p className="font-medium">{doc.document_name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{doc.document_type.replace('_', ' ')}</p>
                  <div className="mt-3 text-sm space-y-1">
                    <p><span className="text-muted-foreground">Issue:</span> {doc.issue_date}</p>
                    <p><span className="text-muted-foreground">Expiry:</span> {doc.expiry_date}</p>
                  </div>
                  {doc.verified && (
                    <Badge variant="outline" className="mt-2 text-green-400 border-green-400">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {compliance.length === 0 && (
            <Card className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No compliance documents uploaded</p>
              <Button className="mt-4" onClick={() => setShowComplianceDialog(true)}>Upload First Document</Button>
            </Card>
          )}
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Sales Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceData?.sales_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(value) => [formatCurrency(value), 'Sales']} />
                      <Line type="monotone" dataKey="sales" stroke="hsl(var(--primary))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Footfall Traffic</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={performanceData?.sales_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                      <Bar dataKey="footfall" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
                <p className="text-xl font-bold">{((performanceData?.conversion_rate || 0) * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Avg Basket Size</p>
                <p className="text-xl font-bold">{formatCurrency(performanceData?.avg_basket_size)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Repeat Customers</p>
                <p className="text-xl font-bold">{((performanceData?.repeat_customer_rate || 0) * 100).toFixed(1)}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Weekly Sales</p>
                <p className="text-xl font-bold">{formatCurrency(sales_summary?.this_week)}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Notices Tab */}
        <TabsContent value="notices" className="space-y-4">
          <h3 className="text-lg font-semibold">Mall Notices ({notices.length})</h3>
          
          {notices.map(notice => (
            <Card key={notice.id} className={`border-l-4 ${notice.notice_type === 'alert' ? 'border-l-red-500' : notice.notice_type === 'event' ? 'border-l-purple-500' : notice.notice_type === 'maintenance' ? 'border-l-orange-500' : 'border-l-blue-500'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${notice.notice_type === 'alert' ? 'bg-red-500/20' : notice.notice_type === 'event' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
                      {notice.notice_type === 'alert' ? <AlertTriangle className="w-5 h-5 text-red-500" /> : notice.notice_type === 'event' ? <Calendar className="w-5 h-5 text-purple-500" /> : <Bell className="w-5 h-5 text-blue-500" />}
                    </div>
                    <div>
                      <p className="font-medium">{notice.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{notice.message}</p>
                      {notice.valid_until && <p className="text-xs text-muted-foreground mt-2">Valid until: {notice.valid_until}</p>}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(notice.created_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          ))}

          {notices.length === 0 && (
            <Card className="p-12 text-center">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No notices</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Raise Service Request</DialogTitle>
            <DialogDescription>Submit a request to mall operations team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category *</Label>
              <Select value={requestForm.category} onValueChange={v => setRequestForm({...requestForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="housekeeping">Housekeeping</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="electrical">Electrical</SelectItem>
                  <SelectItem value="plumbing">Plumbing</SelectItem>
                  <SelectItem value="hvac">HVAC</SelectItem>
                  <SelectItem value="pest_control">Pest Control</SelectItem>
                  <SelectItem value="signage">Signage</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={requestForm.title} onChange={e => setRequestForm({...requestForm, title: e.target.value})} placeholder="Brief description" data-testid="request-title-input" />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={requestForm.description} onChange={e => setRequestForm({...requestForm, description: e.target.value})} placeholder="Detailed description..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={requestForm.priority} onValueChange={v => setRequestForm({...requestForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Input value={requestForm.location_details} onChange={e => setRequestForm({...requestForm, location_details: e.target.value})} placeholder="e.g., Near entrance" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitRequest} disabled={submitting} data-testid="submit-request-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compliance Upload Dialog */}
      <Dialog open={showComplianceDialog} onOpenChange={setShowComplianceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Compliance Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Type *</Label>
              <Select value={complianceForm.document_type} onValueChange={v => setComplianceForm({...complianceForm, document_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fire_noc">Fire NOC</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="trade_license">Trade License</SelectItem>
                  <SelectItem value="gst_certificate">GST Certificate</SelectItem>
                  <SelectItem value="fssai_license">FSSAI License</SelectItem>
                  <SelectItem value="electrical_audit">Electrical Audit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document Name *</Label>
              <Input value={complianceForm.document_name} onChange={e => setComplianceForm({...complianceForm, document_name: e.target.value})} placeholder="e.g., Fire NOC 2026" />
            </div>
            <div>
              <Label>Document URL *</Label>
              <Input value={complianceForm.document_url} onChange={e => setComplianceForm({...complianceForm, document_url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Issue Date</Label>
                <Input type="date" value={complianceForm.issue_date} onChange={e => setComplianceForm({...complianceForm, issue_date: e.target.value})} />
              </div>
              <div>
                <Label>Expiry Date *</Label>
                <Input type="date" value={complianceForm.expiry_date} onChange={e => setComplianceForm({...complianceForm, expiry_date: e.target.value})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComplianceDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadCompliance} disabled={submitting} data-testid="upload-doc-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rate Service</DialogTitle>
            <DialogDescription>How was your experience with this request?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button key={star} onClick={() => setFeedbackForm({...feedbackForm, rating: star})} className="text-3xl focus:outline-none">
                  {star <= feedbackForm.rating ? '★' : '☆'}
                </button>
              ))}
            </div>
            <div>
              <Label>Feedback (optional)</Label>
              <Textarea value={feedbackForm.feedback} onChange={e => setFeedbackForm({...feedbackForm, feedback: e.target.value})} placeholder="Any comments..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitFeedback} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
